import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToSection } from "@/App";

const MENU_LINKS = [
  { id: "home", label: "Home" },
  { id: "tools", label: "Our Tools" },
  { id: "about", label: "About Proxima" },
  { id: "contact", label: "Contact" },
];

const Hero = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (id) => {
    setMenuOpen(false);
    setTimeout(() => scrollToSection(id), 250);
  };

  return (
  <section id="home" data-testid="hero-section" className="relative aspect-[9/16] w-full overflow-hidden md:aspect-auto md:h-[100svh] md:min-h-[600px]">
    <img
      data-testid="hero-artwork-mobile"
      src="/hero-artwork.jpg"
      alt="Proxima Centauri Tools — Tools Built From Real Jobsite Problems. A new American tool company."
      className="absolute inset-0 h-full w-full object-cover object-center md:hidden"
    />
    <img
      data-testid="hero-artwork-backdrop"
      src="/hero-artwork.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 hidden h-full w-full scale-110 object-cover object-[center_35%] blur-[36px] brightness-[0.4] md:block"
    />
    <div
      className="absolute left-1/2 top-[47%] hidden aspect-[941/724] h-[86svh] max-h-full -translate-x-1/2 -translate-y-1/2 overflow-hidden md:block"
      style={{ boxShadow: "0 0 120px -20px rgba(230,0,0,0.45)" }}
    >
      <img
        data-testid="hero-artwork-desktop"
        src="/hero-artwork.jpg"
        alt="Proxima Centauri Tools — official logo"
        className="absolute left-0 w-full object-cover"
        style={{ height: "231%", top: "-14%" }}
      />
    </div>
    <button
      data-testid="hero-cta-tools-overlay"
      onClick={() => scrollToSection("tools")}
      aria-label="Our Tools"
      className="absolute left-[8%] top-[90%] h-[5.5%] w-[38%] cursor-pointer rounded-sm transition-[box-shadow] duration-300 hover:shadow-[0_0_50px_-4px_rgba(230,0,0,0.85)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-ember md:hidden"
    />
    <button
      data-testid="hero-cta-tools"
      onClick={() => scrollToSection("tools")}
      className="group absolute bottom-12 left-12 hidden overflow-hidden bg-ember px-9 py-4 font-display text-xl font-semibold uppercase tracking-[0.15em] text-white transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_44px_-6px_rgba(230,0,0,0.65)] md:block"
    >
      <span className="relative z-10 flex items-center gap-2">
        Our Tools <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </span>
      <span className="absolute inset-0 -translate-x-full bg-white/20 transition-transform duration-500 group-hover:translate-x-0" />
    </button>
    <div className="sr-only">
      <p>A New American Tool Company</p>
      <h1 data-testid="hero-headline">Tools Built From Real Jobsite Problems</h1>
      <p>
        Proxima Centauri Tools engineers innovative tools for the construction industry —
        starting with the problems nobody else bothered to fix.
      </p>
      <p>The Mark of the Trades</p>
    </div>
    <button
      data-testid="hero-menu-open-button"
      onClick={() => setMenuOpen(true)}
      aria-label="Open menu"
      className="group absolute right-5 top-5 flex h-11 w-11 cursor-pointer items-center justify-center border border-white/15 bg-coal/50 backdrop-blur-md transition-[border-color,box-shadow] duration-300 hover:border-ember/60 hover:shadow-[0_0_24px_-6px_rgba(230,0,0,0.6)] lg:right-8 lg:top-8"
    >
      <Menu size={22} className="text-white transition-colors duration-300 group-hover:text-ember" />
    </button>

    <AnimatePresence>
      {menuOpen && (
        <motion.div
          data-testid="hero-mobile-menu"
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-[100] flex flex-col bg-coal/95 backdrop-blur-2xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-end px-6 py-5">
            <button
              data-testid="hero-menu-close-button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="flex h-11 w-11 items-center justify-center border border-white/10 bg-coal/40 text-white backdrop-blur-md"
            >
              <X size={24} />
            </button>
          </div>
          <nav
            className="flex flex-1 flex-col justify-center gap-2 px-8"
            aria-label="Mobile"
          >
            {MENU_LINKS.map((link, i) => (
              <motion.button
                key={link.id}
                data-testid={`hero-mobile-nav-${link.id}`}
                onClick={() => go(link.id)}
                className="text-left font-display text-6xl font-semibold uppercase leading-[1.05] text-white/90"
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
              >
                <span className="mr-3 text-xl text-ember">0{i + 1}</span>
                {link.label}
              </motion.button>
            ))}
          </nav>
          <p className="px-8 pb-10 font-body text-xs uppercase tracking-[0.3em] text-ash">
            Tools Built From Real Jobsite Problems
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </section>
  );
};

export default Hero;
